import { Eye, EyeOff, LogOut, Shield, User as UserIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/features/auth';
import {
  SelfPresenceBadge,
  usePresenceSettings,
  useUpdatePresenceSettings,
} from '@/features/presence';
import { useSession } from '@/stores/session';

function initials(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function UserMenu() {
  const user = useSession((s) => s.user);
  const { signOut } = useAuth();
  const { data: presence } = usePresenceSettings();
  const updatePresence = useUpdatePresenceSettings();
  if (!user) return null;

  const displayName =
    [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username || user.email;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full" aria-label="Open user menu">
          <Avatar className="size-8">
            {user.photo ? <AvatarImage src={user.photo} alt="" /> : null}
            <AvatarFallback>{initials(displayName)}</AvatarFallback>
            <SelfPresenceBadge />
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col">
          <span className="truncate font-medium">{displayName}</span>
          <span className="text-muted-foreground truncate text-xs font-normal">{user.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/me/account">
            <UserIcon />
            Account
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/account/security">
            <Shield />
            Security
          </Link>
        </DropdownMenuItem>
        {presence ? (
          // A toggle whose state is the eye itself — open while visible, crossed
          // out while appearing offline — so no check mark (FE-ADR-068).
          <DropdownMenuItem
            role="menuitemcheckbox"
            aria-checked={presence.appear_offline}
            disabled={updatePresence.isPending}
            onSelect={(e) => {
              // Keep the menu open so the eye visibly changes.
              e.preventDefault();
              updatePresence.mutate({ appear_offline: !presence.appear_offline });
            }}
          >
            {presence.appear_offline ? <EyeOff /> : <Eye />}
            Appear offline
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onSelect={() => void signOut()}>
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
